import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CheckCircle2, AlertTriangle, X } from 'lucide-react';

const formatValue = (v) => {
  if (v === null || v === undefined) return <span className='italic opacity-70'>none</span>;
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
};

const PreviewCard = ({ preview, params, onConfirm, onCancel, isExecuting }) => {
  const [acknowledged, setAcknowledged] = useState(preview?.warnings?.length === 0);
  const idempotencyKey = useMemo(
    () => `ux_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`,
    []
  );

  if (!preview || preview.error) {
    return (
      <div className='rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 p-3 text-xs text-red-700 dark:text-red-300'>
        <AlertTriangle className='mr-1 inline h-3 w-3' />
        {preview?.error || 'Preview failed.'}
      </div>
    );
  }

  const fieldKeys = Array.from(
    new Set([
      ...Object.keys(preview.before || {}),
      ...Object.keys(preview.after || {})
    ])
  ).filter((k) => (preview.before?.[k] ?? null) !== (preview.after?.[k] ?? null));

  return (
    <div className='rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1D2838] p-4 shadow-sm'>
      <div className='flex items-start justify-between gap-3'>
        <div>
          <p className='text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400'>
            Preview · {preview.action}
          </p>
          <p className='mt-1 text-sm font-semibold text-gray-900 dark:text-white'>{preview.summary}</p>
        </div>
        <span className='shrink-0 rounded-full bg-amber-100 dark:bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300'>
          Awaiting confirm
        </span>
      </div>

      {fieldKeys.length > 0 ? (
        <div className='mt-3 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700'>
          <table className='w-full text-xs'>
            <thead className='bg-gray-50 dark:bg-[#0F1A2A] text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400'>
              <tr>
                <th className='px-3 py-2 text-left font-semibold'>Field</th>
                <th className='px-3 py-2 text-left font-semibold'>Before</th>
                <th className='px-3 py-2 text-left font-semibold'>After</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-100 dark:divide-gray-800'>
              {fieldKeys.map((k) => (
                <tr key={k}>
                  <td className='px-3 py-1.5 font-medium text-gray-900 dark:text-white'>{k}</td>
                  <td className='px-3 py-1.5 text-gray-500 dark:text-gray-400'>
                    {formatValue(preview.before?.[k])}
                  </td>
                  <td className='px-3 py-1.5 font-medium text-gray-900 dark:text-white'>
                    {formatValue(preview.after?.[k])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className='mt-3 text-xs text-gray-500 dark:text-gray-400'>
          No fields would change — confirming this is a no-op.
        </p>
      )}

      {preview.warnings?.length ? (
        <ul className='mt-3 space-y-1 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-2.5 text-[11px] text-amber-800 dark:text-amber-200'>
          {preview.warnings.map((w) => (
            <li key={w} className='flex items-start gap-1.5'>
              <AlertTriangle className='mt-0.5 h-3 w-3 shrink-0' />
              <span>{w}</span>
            </li>
          ))}
          <li className='flex items-center gap-2 pt-1 border-t border-amber-200/60 dark:border-amber-500/20 mt-1'>
            <input
              type='checkbox'
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className='h-3 w-3 accent-amber-600'
            />
            I&apos;ve read the warnings above.
          </li>
        </ul>
      ) : null}

      <div className='mt-4 flex items-center justify-end gap-2'>
        <button
          type='button'
          onClick={onCancel}
          disabled={isExecuting}
          className='inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition'
        >
          <X className='h-3 w-3' />
          Cancel
        </button>
        <button
          type='button'
          disabled={!acknowledged || isExecuting}
          onClick={() =>
            onConfirm({
              action: preview.action,
              params,
              idempotency_key: idempotencyKey
            })
          }
          className='inline-flex items-center gap-1.5 rounded-lg bg-primary-green px-3 py-1.5 text-xs font-semibold text-primary-green-fg hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed transition'
        >
          <CheckCircle2 className='h-3 w-3' />
          {isExecuting ? 'Applying...' : 'Confirm & apply'}
        </button>
      </div>
    </div>
  );
};

PreviewCard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  preview: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  params: PropTypes.object,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isExecuting: PropTypes.bool
};

PreviewCard.defaultProps = {
  preview: null,
  params: {},
  isExecuting: false
};

export default PreviewCard;
